/**
 * El carro, compartido por toda la tienda (CHK-01, CHK-02).
 *
 * Es un contexto y no un hook suelto porque el carro lo tocan a la vez el globo
 * de la cabecera, el boton de cada tarjeta, el de la ficha y la pantalla del
 * carro. Con un hook por componente cada uno tendria su propia copia y el globo
 * no se enteraria de nada.
 *
 * Arranca leyendo lo que haya en el navegador —para que la primera pintada ya
 * muestre el numero correcto y no salte de 0 a 3— y despues pregunta al catalogo
 * cuales de esos productos siguen publicados. Esa segunda pasada es la que
 * descarta lo que ya no existe y refresca precios; hasta que responde, el
 * comprador ve lo que guardo, que es mejor que un carro en blanco.
 *
 * El carro NO se manda al servidor: vive en este navegador hasta el momento de
 * pagar, que es cuando se verifica el stock contra Random (CHK-05).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import type { Carro, ProductoResumen } from '@elcarpintero/compartido';

import { obtenerProductosVigentes } from '../servicios/catalogo.servicio';
import { guardarCarro, leerCarroGuardado } from '../utilidades/almacen-carro';
import * as carros from '../utilidades/carro';
import { ContextoCarro, type ValorCarro } from './carro-contexto';

export function ProveedorCarro({ children }: { children: ReactNode }) {
  const [carro, setCarro] = useState<Carro>(leerCarroGuardado);

  // Cada cambio del carro se guarda. Es el unico lugar que escribe en el
  // almacenamiento: asi no hay forma de mover una linea y olvidar persistirla.
  useEffect(() => {
    guardarCarro(carro);
  }, [carro]);

  // Una sola vez por visita: que sigue publicado de lo que estaba guardado.
  useEffect(() => {
    const guardado = leerCarroGuardado();
    if (guardado.items.length === 0) return;

    const control = new AbortController();

    obtenerProductosVigentes(
      guardado.items.map((item) => item.producto.sku),
      control.signal,
    )
      .then((vigentes) => {
        if (control.signal.aborted) return;
        setCarro((actual) => carros.conciliar(actual, vigentes));
      })
      .catch(() => {
        // Sin respuesta del catalogo se deja el carro tal como estaba: quitarle
        // lineas al comprador porque fallo una peticion seria peor.
      });

    return () => {
      control.abort();
    };
  }, []);

  const agregar = useCallback((producto: ProductoResumen, cantidad = 1) => {
    setCarro((actual) => carros.agregar(actual, producto, cantidad));
  }, []);

  const cambiarCantidad = useCallback((sku: string, cantidad: number) => {
    setCarro((actual) => carros.cambiarCantidad(actual, sku, cantidad));
  }, []);

  const quitar = useCallback((sku: string) => {
    setCarro((actual) => carros.quitar(actual, sku));
  }, []);

  const vaciar = useCallback(() => {
    setCarro(carros.CARRO_VACIO);
  }, []);

  const valor = useMemo<ValorCarro>(
    () => ({
      carro,
      unidades: carros.unidadesEn(carro),
      agregar,
      cambiarCantidad,
      quitar,
      vaciar,
    }),
    [carro, agregar, cambiarCantidad, quitar, vaciar],
  );

  return <ContextoCarro.Provider value={valor}>{children}</ContextoCarro.Provider>;
}
